/**
 * Error utility functions for standardized error handling
 */

import { ErrorCategory, ErrorSeverity, ErrorDetails } from '../types/error';
import { errorLogger } from '../services/errorLogger';

/**
 * Categorize error based on error type and message
 */
export function categorizeError(error: Error): ErrorCategory {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Network errors
  if (name.includes('network') || message.includes('network') ||
      message.includes('fetch') || message.includes('timeout') ||
      message.includes('connection') || name === 'apierror') {
    return ErrorCategory.NETWORK;
  }

  // Authentication errors
  if (message.includes('unauthorized') || message.includes('authentication') ||
      message.includes('login') || message.includes('token')) {
    return ErrorCategory.AUTHENTICATION;
  }

  // Authorization errors
  if (message.includes('forbidden') || message.includes('permission') ||
      message.includes('access denied') || message.includes('authorization')) {
    return ErrorCategory.AUTHORIZATION;
  }

  // Validation errors
  if (message.includes('validation') || message.includes('invalid') ||
      message.includes('required') || name.includes('validation')) {
    return ErrorCategory.VALIDATION;
  }

  // React component errors
  if (name.includes('react') || message.includes('component') ||
      message.includes('render') || name === 'invariant violation') {
    return ErrorCategory.UI_COMPONENT;
  }

  // System errors
  if (name.includes('system') || message.includes('system') ||
      name === 'referenceerror' || name === 'typeerror') {
    return ErrorCategory.SYSTEM;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Determine error severity based on error type and context
 */
export function determineErrorSeverity(error: Error, category: ErrorCategory): ErrorSeverity {
  const message = error.message.toLowerCase();

  // Critical errors that break the app
  if (message.includes('chunk load error') || message.includes('script error') ||
      error.name === 'ChunkLoadError' || category === ErrorCategory.SYSTEM) {
    return ErrorSeverity.CRITICAL;
  }

  // High severity errors
  if (category === ErrorCategory.AUTHENTICATION || category === ErrorCategory.NETWORK ||
      message.includes('server error') || message.includes('500')) {
    return ErrorSeverity.HIGH;
  }

  // Medium severity errors
  if (category === ErrorCategory.AUTHORIZATION || category === ErrorCategory.VALIDATION ||
      category === ErrorCategory.UI_COMPONENT) {
    return ErrorSeverity.MEDIUM;
  }

  // Default to low severity
  return ErrorSeverity.LOW;
}

/**
 * Check if error is recoverable
 */
export function isErrorRecoverable(error: Error, category: ErrorCategory): boolean {
  const message = error.message.toLowerCase();

  // Non-recoverable errors
  if (message.includes('chunk load error') || message.includes('script error') ||
      error.name === 'ChunkLoadError') {
    return false;
  }

  // Network errors are usually recoverable
  if (category === ErrorCategory.NETWORK) {
    return true;
  }

  // UI component errors are usually recoverable
  if (category === ErrorCategory.UI_COMPONENT) {
    return true;
  }

  // Validation errors are recoverable
  if (category === ErrorCategory.VALIDATION) {
    return true;
  }

  // System errors might not be recoverable
  if (category === ErrorCategory.SYSTEM) {
    return false;
  }

  // Default to recoverable
  return true;
}

/**
 * Create standardized error details from Error object
 */
export function createErrorDetails(error: Error): ErrorDetails {
  const category = categorizeError(error);
  const severity = determineErrorSeverity(error, category);
  const recoverable = isErrorRecoverable(error, category);

  return errorLogger.createErrorDetails(error, category, severity, recoverable);
}

/**
 * Create user-friendly error message
 */
export function createUserFriendlyMessage(error: Error, category: ErrorCategory): string {
  switch (category) {
    case ErrorCategory.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    
    case ErrorCategory.AUTHENTICATION:
      return 'Your session has expired. Please log in again to continue.';
    
    case ErrorCategory.AUTHORIZATION:
      return 'You don\'t have permission to access this resource. Please contact your administrator.';
    
    case ErrorCategory.VALIDATION:
      return 'Please check your input and try again. Some required fields may be missing or invalid.';
    
    case ErrorCategory.UI_COMPONENT:
      return 'Something went wrong with the page display. Try refreshing the page.';
    
    case ErrorCategory.SYSTEM:
      return 'A system error occurred. Our team has been notified and is working to fix this issue.';
    
    case ErrorCategory.BUSINESS_LOGIC:
      return 'Unable to complete this action due to business rule constraints.';
    
    default:
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}

/**
 * Get retry delay based on attempt count (exponential backoff)
 */
export function getRetryDelay(attemptCount: number, baseDelay: number = 1000): number {
  return Math.min(baseDelay * Math.pow(2, attemptCount), 30000); // Max 30 seconds
}

/**
 * Sanitize error for logging (remove sensitive data)
 */
export function sanitizeError(error: Error): Error {
  const sanitized = new Error(error.message);
  sanitized.name = error.name;
  sanitized.stack = error.stack;

  // Remove sensitive information from stack trace and message
  if (sanitized.stack) {
    sanitized.stack = sanitized.stack
      .replace(/token=[\w-]+/gi, 'token=***')
      .replace(/password=[\w-]+/gi, 'password=***')
      .replace(/key=[\w-]+/gi, 'key=***');
  }

  if (sanitized.message) {
    sanitized.message = sanitized.message
      .replace(/token=[\w-]+/gi, 'token=***')
      .replace(/password=[\w-]+/gi, 'password=***')
      .replace(/key=[\w-]+/gi, 'key=***');
  }

  return sanitized;
}

/**
 * Check if error should be reported to remote service
 */
export function shouldReportError(error: Error, category: ErrorCategory, severity: ErrorSeverity): boolean {
  // Don't report low severity validation errors
  if (category === ErrorCategory.VALIDATION && severity === ErrorSeverity.LOW) {
    return false;
  }

  // Don't report network timeouts in development
  if (import.meta.env.DEV && category === ErrorCategory.NETWORK) {
    return false;
  }

  // Report all medium and above severity errors
  return severity !== ErrorSeverity.LOW;
}

/**
 * Extract useful error information for debugging
 */
export function extractErrorInfo(error: Error): {
  message: string;
  stack?: string;
  name: string;
  cause?: Error;
  timestamp: string;
  url: string;
  userAgent: string;
} {
  return {
    message: error.message,
    stack: error.stack,
    name: error.name,
    cause: (error as any).cause,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
}

/**
 * Check if error is a chunk loading error (common in SPAs)
 */
export function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError' || 
         error.message.includes('Loading chunk') ||
         error.message.includes('Loading CSS chunk');
}

/**
 * Handle chunk load errors by reloading the page
 */
export function handleChunkLoadError(): void {
  console.warn('Chunk load error detected, reloading page...');
  window.location.reload();
}

/**
 * Format error for display in UI
 */
export function formatErrorForDisplay(errorDetails: ErrorDetails): {
  title: string;
  message: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  timestamp: string;
} {
  const userMessage = createUserFriendlyMessage(
    new Error(errorDetails.message), 
    errorDetails.category
  );

  return {
    title: `${errorDetails.category.replace('_', ' ').toUpperCase()} Error`,
    message: userMessage,
    severity: errorDetails.severity,
    category: errorDetails.category,
    timestamp: errorDetails.context?.timestamp || new Date().toISOString(),
  };
}